import CheckCircle from 'assets/check-circle.svg';
import ErrorCircle from 'assets/exclamation-x-circle.svg';
import SearchInput from 'components/SearchInput';
import TabGroup from 'components/TabGroup';
import { useAuth } from 'contexts/AuthProvider';
import { useConfig } from 'contexts/ConfigProvider';
import { useToast } from 'contexts/ToastProvider';
import { User } from 'entities/user.entity';
import PortalProfile from 'pages/PortalProfile';
import { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { BuildVersionList, addSiteIdPrefix } from 'utils/helper';
import Members, { MerbersRefType } from './Members';
import EditMemberDrawer from './Members/EditMemberDrawer';
import MyAccount from './MyAccount';

export enum Role {
  Owner = 'owner',
  Manager = 'manager',
  Viewer = 'viewer',
}

const Profile = ({ tab }: { tab: number }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { addToast } = useToast();
  const { buildVersion } = useConfig();
  const { user, putUserRole } = useAuth();
  const [globalFilter, setGlobalFilter] = useState('');
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [userData, setUserData] = useState<User>({} as User);
  const addSiteIdPrefix_ = addSiteIdPrefix();
  const navigateUrl = [`${addSiteIdPrefix_}/profile/myAccount`, `${addSiteIdPrefix_}/profile/members`];
  const labels: { icon: string; name: string; width: string }[] = [
    { icon: '', name: t('profile.myAccountPageTitle'), width: '' },
    ...(user.role === Role.Owner ? [{ icon: '', name: t('profile.membersPageTitle'), width: '' }] : []),
  ];
  const memberRef = useRef<MerbersRefType>({} as MerbersRefType);
  const methods = useForm();

  const onSubmit = (data: any) => {
    putUserRole(userData.id, { role: data.role })
      .then(() => {
        setOpenDrawer(false);
        memberRef.current.getMembers();
        addToast({
          title: t('toast.updatedSuccessfully'),
          message: '',
          color: 'bg-toast-success-50',
          icon: CheckCircle,
          text: 'text-toast-success-800',
          svg: 'toastIcon-success',
        });
      })
      .catch((error) => {
        addToast({
          title: t('toast.updatedFailed'),
          message: error.response.data.message ?? '',
          color: 'bg-toast-error-50',
          icon: ErrorCircle,
          text: 'text-toast-error-800',
          svg: 'toastIcon-error',
        });
      });
  };

  return (
    <div className="drawer drawer-end h-full">
      <input id="addMemberDrawer" type="checkbox" className="drawer-toggle" checked={openDrawer} readOnly />
      <div className="drawer-content">
        <div className="card-title px-4 py-4 bg-white shadow flex justify-between">
          <h2>{t('profile.pageTitle')}</h2>
          <div className="float-right pr-4">
            {location.pathname.includes('/profile/members') && (
              <SearchInput
                value={globalFilter ?? ''}
                onChange={(value) => setGlobalFilter(String(value))}
                className="input border-0 w-full"
                placeholder={t('component.searchInput')}
              />
            )}
          </div>
        </div>
        <div>
          <TabGroup
            initIndex={tab}
            labels={labels.map((l) => (
              <div className={`flex items-center space-x-1 ${l.width}`}>
                <div className="text-sky-800">{l.name}</div>
              </div>
            ))}
            childrens={[
              ...(buildVersion === BuildVersionList.portal ? [<PortalProfile />] : [<MyAccount />]),
              <Members
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                setOpenDrawer={setOpenDrawer}
                setUserData={setUserData}
                ref={memberRef}
              />,
            ]}
            tabClass="tab tab-bordered h-10 w-1/4"
            otherClass="flex-row-reverse"
            navigateUrl={navigateUrl}
          />
        </div>
      </div>
      <div
        className="drawer-side"
        onClick={() => {
          methods.reset();
          setOpenDrawer(false);
        }}
      >
        <label htmlFor="addMemberDrawer" className="drawer-overlay"></label>
        <div
          className="w-1/5 min-w-max bg-white overflow-y-auto"
          onClick={(event) => {
            // 防止觸發外層 click event(setOpenDrawer)
            event.stopPropagation();
          }}
        >
          <>
            <FormProvider {...methods}>
              <form className="flex flex-col h-full" onSubmit={methods.handleSubmit(onSubmit)}>
                <EditMemberDrawer userData={userData} setOpenDrawer={setOpenDrawer} />
              </form>
            </FormProvider>
          </>
        </div>
      </div>
    </div>
  );
};

export default Profile;
